import React, { useState } from "react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Tent, Loader2, CheckCircle2, Plus } from "lucide-react";

/**
 * Admin card to bulk-ingest UK campsites from Pitchup's public sitemap that
 * aren't yet in our DB. Each run inserts up to `limit` new sites (geocoded
 * via Nominatim at 1 req/sec, so 50 sites ≈ 60s). Re-run until
 * `remaining_to_ingest` reaches 0.
 */
const COUNTRIES = [
  { value: "", label: "All UK" },
  { value: "England", label: "England" },
  { value: "Wales", label: "Wales" },
  { value: "Scotland", label: "Scotland" },
  { value: "Northern_Ireland", label: "Northern Ireland" },
];

export default function PitchupIngestCard() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  // iter88: backend clamps `limit` to ≤12. Each candidate may retry 3
  // Nominatim query variants at 1 req/sec, so worst-case batch time is
  // `limit × 3 × 1.05s + http overhead`. The platform's 60s proxy
  // timeout means anything above ~12 risks a 520 Cloudflare timeout.
  // Sitemap is now cached for 6h so repeat runs skip the slow crawl.
  const [limit, setLimit] = useState(10);
  const [country, setCountry] = useState("");

  const run = async () => {
    if (running) return;
    setRunning(true);
    setResult(null);
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (country) params.set("country", country);
      const { data } = await api.post(`/admin/pitchup/ingest-new?${params.toString()}`);
      setResult(data);
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`${data.inserted} new Pitchup campsites added.`, { duration: 5000 });
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="border-2 hairline-strong bg-gradient-to-br from-[#f0f9ff] to-white p-6 mb-8"
      data-testid="pitchup-ingest-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="eyebrow text-[#0369a1] flex items-center gap-1.5">
            <Tent className="w-3 h-3" /> Bulk-ingest from Pitchup
          </div>
          <h3 className="font-display text-xl mt-1">Add bookable Pitchup campsites</h3>
          <p className="text-sm text-[#444] leading-snug mt-2 max-w-[600px]">
            Crawls Pitchup's public sitemap (~10,000 UK sites) and inserts
            new bookable campsites into our DB with name, region, and a
            geocoded lat/lng (via Nominatim). Sitemap results are cached
            for 6 hours so repeat clicks are fast. Idempotent — re-run
            until <em>remaining_to_ingest</em> reaches 0. Capped at 12
            per run (Nominatim's 1 req/sec + 60s proxy timeout). ~5-10
            clicks needed to top up a fresh DB.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-end gap-3 flex-wrap">
        <label className="block">
          <span className="eyebrow block mb-1">Country</span>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="border hairline px-3 py-2 text-sm bg-white"
            data-testid="pitchup-ingest-country"
            disabled={running}
          >
            {COUNTRIES.map((c) => (
              <option key={c.value || "all"} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="eyebrow block mb-1">Batch size</span>
          <input
            type="number"
            min={1}
            max={12}
            value={limit}
            onChange={(e) => setLimit(Math.max(1, Math.min(12, Number(e.target.value) || 10)))}
            className="border hairline px-3 py-2 text-sm bg-white w-28 mono"
            data-testid="pitchup-ingest-limit"
            disabled={running}
          />
        </label>
        <button
          onClick={run}
          disabled={running}
          className="btn-primary text-sm px-6 py-3 inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="pitchup-ingest-run"
        >
          {running ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
              Ingesting…
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" strokeWidth={2.5} />
              Run ingest
            </>
          )}
        </button>
      </div>

      {result && (
        <div
          className="mt-5 border hairline bg-white p-4"
          data-testid="pitchup-ingest-result"
        >
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-[#22c55e]" strokeWidth={2.5} />
            <span className="font-display text-base">Ingest run complete</span>
          </div>
          {result.error ? (
            <div className="text-sm text-[#b91c1c] mono">{result.error}</div>
          ) : (
            <>
              <ul className="text-sm space-y-1.5 mono text-[#333]">
                <li>
                  <strong>{result.inserted}</strong> new campsites inserted
                </li>
                <li>
                  <strong>{result.skipped_no_geocode ?? 0}</strong> skipped (no geocode)
                </li>
                <li>
                  Sitemap UK pool: <strong>{result.uk_slugs_in_sitemap?.toLocaleString()}</strong>
                </li>
                <li>
                  Remaining to ingest:{" "}
                  <strong>{(result.remaining_to_ingest ?? 0).toLocaleString()}</strong>
                </li>
              </ul>
              {result.samples?.length ? (
                <div className="mt-4">
                  <div className="eyebrow mb-2">First 5 added</div>
                  <ul className="text-xs space-y-1 mono text-[#666]">
                    {result.samples.map((s, i) => (
                      <li key={i}>
                        • <strong className="text-[#222]">{s.name}</strong> — {s.region}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>
  );
}
